import React, { useLayoutEffect, useRef } from 'react';
import Message from './Message';
import QAMessage from './QAMessage';
import QuestionMessage from './QuestionMessage';
import { motion } from 'framer-motion';
import usePubNubMessages from '../hooks/usePubNubMessages';
import { useChat } from '../modules/ChatPane';
import { sortMessagesByVotes } from '../helpers/pubNubFunctions';

// Messsage factory for streamchat pane
// Renders chat/qa/poll messages

export default function Messages({ channeltype, showVoting, showing, moderateFlow = false, room = false }) {
  const {
    context: { content, isOpen, selectedTab, activeMessenger },
  } = useChat();

  if (!room) {
    room = content.roomForChannelType(channeltype);
  }
  const updateRoom = content.roomForChannelType('update');

  const [messages] = usePubNubMessages(room, updateRoom);

  const [animationComplete, setAnimationComplete] = React.useState(false);

  // autoscrolling functionality
  const autoScrollRef = useRef(true);
  const autoScrollContainerRef = useRef(null);

  const onScroll = () => {
    if (autoScrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = autoScrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        autoScrollRef.current = true;
      } else {
        autoScrollRef.current = false;
      }
    }
  };

  useLayoutEffect(() => {
    if (autoScrollRef.current) {
      // this is a known usecase for setTImeout - the dom has painted (useLayoutEffect) but hasn't
      // fully rendered so by using a setTimeout we are giving it microsectonds to render while also
      // adding the checking of scrollHeight and clientHeight to the microtask queue so they can be accurate
      setTimeout(() => {
        autoScrollContainerRef.current.scrollTop =
          autoScrollContainerRef.current?.scrollHeight - autoScrollContainerRef.current?.clientHeight;
      }, 100);
    }
  }, [messages, isOpen, selectedTab, activeMessenger]);

  let messageBody = <div className={`no_messages_text`}>No messages yet</div>;

  if (messages?.length > 0) {
    switch (channeltype) {
      //@ts-ignore
      case OTSessionContent.CHANNELTYPE_QA:
        messageBody = messages.sort(sortMessagesByVotes).map((message, i) => {
          if ((moderateFlow && !message.actions?.posted?.true) || message.message.type === 'update') {
            return <div key={message.timetoken} style={{ display: 'none' }}></div>;
          }
          return (
            <QAMessage
              key={message.timetoken}
              message={message}
              channeltype={channeltype}
              showVoting={showVoting}
              room={room}
              moderatedQAFlow={false}
              containerRef={autoScrollContainerRef}
              content={content}
            />
          );
        });
        break;
      //@ts-ignore
      case OTSessionContent.CHANNELTYPE_POLL:
        messageBody = messages.map((message, i) => {
          if (message?.message.data?.modType == 'updatepoll') {
            return null;
          }
          if (!message?.message?.data) {
            return null;
          }
          return (
            <QuestionMessage
              key={message.timetoken}
              showing={showing && animationComplete}
              message={message}
              questions={content.Questions}
              room={room}
              channeltype={channeltype}
              containerRef={autoScrollContainerRef}
            />
          );
        });
        break;
      //@ts-ignore
      case OTSessionContent.CHANNELTYPE_CHAT:
      //@ts-ignore
      case OTSessionContent.CHANNELTYPE_MOD:
      //@ts-ignore
      case OTSessionContent.CHANNELTYPE_COHORT:
        messageBody = messages
          .filter(message => {
            if (message.actions?.flag?.kicked?.length > 0) {
              return false;
            } else {
              return true;
            }
          })
          .map((message, i) => {
            return (
              <Message
                key={message.timetoken}
                message={message}
                channeltype={channeltype}
                containerRef={autoScrollContainerRef}
              />
            );
          });
        break;
      default:
    }
  }

  return (
    <motion.div
      className={`messages_scroll_container`}
      variants={messagesVariants}
      animate={showing ? `visible` : `hidden`}
      ref={autoScrollContainerRef}
      onScroll={onScroll}
      onAnimationComplete={definition => {
        if (definition === `visible`) {
          setAnimationComplete(true);
        }
        if (definition === `hidden`) {
          setAnimationComplete(false);
        }
      }}
    >
      {messageBody}
    </motion.div>
  );
}

const messagesVariants = {
  hidden: {
    opacity: 0,
    display: 'none',
  },
  visible: {
    opacity: 1,
    display: 'block',
  },
};
